import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  nextAvailableTransectName,
  type Transect,
  type TransectStation,
} from "../model/inp/transects.ts";
import { handleGridArrowNav, handleGridDeleteRow } from "./gridKeyboardNav.ts";
import { useT } from "../i18n/hooks.ts";
import { PlusIcon } from "./PlusIcon.tsx";
import { NumberField, NumberInput } from "./fields.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_SERIES_PRIMARY,
  CHART_TOOLTIP_STYLE,
} from "./chartTheme.ts";

export function TransectsEditor({
  transects,
  onChange,
  selectedName,
  onSelect,
}: {
  transects: Transect[];
  onChange: (transects: Transect[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteTransect,
    rename: renameSelected,
    update: updateSelected,
  } = useNamedCollection(transects, onChange, selectedName, onSelect);

  function addTransect() {
    const name = nextAvailableTransectName(new Set(transects.map((tr) => tr.name)));
    add({
      name,
      nLeft: 0,
      nRight: 0,
      nChannel: 0,
      xLeftBank: 0,
      xRightBank: 0,
      lengthFactor: 1,
      stationMultiplier: 1,
      elevationOffset: 0,
      stations: [{ elevation: 0, station: 0 }],
    });
  }

  function removeStation(index: number) {
    updateSelected((tr) =>
      tr.stations.length <= 1 ? tr : { ...tr, stations: tr.stations.filter((_, j) => j !== index) }
    );
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={transects}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addTransect}
        addLabel={t("transect.addTransect")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("transect.noTransectSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteTransect(selected.name)}
              deleteLabel={t("transect.deleteTransect")}
            />
            <div className="named-collection-fields">
              <NumberField
                label={t("transect.nLeft")}
                value={selected.nLeft}
                onChange={(v) => updateSelected((tr) => ({ ...tr, nLeft: v }))}
              />
              <NumberField
                label={t("transect.nRight")}
                value={selected.nRight}
                onChange={(v) => updateSelected((tr) => ({ ...tr, nRight: v }))}
              />
              <NumberField
                label={t("transect.nChannel")}
                value={selected.nChannel}
                onChange={(v) => updateSelected((tr) => ({ ...tr, nChannel: v }))}
              />
              <NumberField
                label={t("transect.xLeftBank")}
                value={selected.xLeftBank}
                onChange={(v) => updateSelected((tr) => ({ ...tr, xLeftBank: v }))}
              />
              <NumberField
                label={t("transect.xRightBank")}
                value={selected.xRightBank}
                onChange={(v) => updateSelected((tr) => ({ ...tr, xRightBank: v }))}
              />
              <NumberField
                label={t("transect.lengthFactor")}
                value={selected.lengthFactor}
                onChange={(v) => updateSelected((tr) => ({ ...tr, lengthFactor: v }))}
              />
              <NumberField
                label={t("transect.stationMultiplier")}
                value={selected.stationMultiplier}
                onChange={(v) => updateSelected((tr) => ({ ...tr, stationMultiplier: v }))}
              />
              <NumberField
                label={t("transect.elevationOffset")}
                value={selected.elevationOffset}
                onChange={(v) => updateSelected((tr) => ({ ...tr, elevationOffset: v }))}
              />
            </div>
            <div className="curve-editor-body">
              <div className="curve-editor-table">
                <div className="named-collection-points-table-wrap">
                  <table className="named-collection-points-table">
                    <thead>
                      <tr>
                        <th>{t("transect.station")}</th>
                        <th>{t("transect.elevation")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.stations.map((s, i) => (
                        <tr key={i}>
                          <td>
                            <NumberInput
                              value={s.station}
                              onChange={(v) =>
                                updateSelected((tr) => ({
                                  ...tr,
                                  stations: tr.stations.map((p, j) =>
                                    j === i ? { ...p, station: v } : p
                                  ),
                                }))
                              }
                              onKeyDown={(e) => {
                                handleGridArrowNav(e);
                                handleGridDeleteRow(e, () => removeStation(i));
                              }}
                            />
                          </td>
                          <td>
                            <NumberInput
                              value={s.elevation}
                              onChange={(v) =>
                                updateSelected((tr) => ({
                                  ...tr,
                                  stations: tr.stations.map((p, j) =>
                                    j === i ? { ...p, elevation: v } : p
                                  ),
                                }))
                              }
                              onKeyDown={(e) => {
                                handleGridArrowNav(e);
                                handleGridDeleteRow(e, () => removeStation(i));
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  className="button"
                  onClick={() =>
                    updateSelected((tr) => {
                      const last = tr.stations[tr.stations.length - 1];
                      const newStation: TransectStation = {
                        elevation: last?.elevation ?? 0,
                        station: (last?.station ?? 0) + 1,
                      };
                      return { ...tr, stations: [...tr.stations, newStation] };
                    })
                  }
                >
                  <PlusIcon />
                  {t("transect.addStation")}
                </button>
              </div>
              <div className="curve-editor-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={selected.stations}
                    margin={{ top: 4, right: 12, bottom: 0, left: -12 }}
                  >
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="station"
                      type="number"
                      domain={["dataMin", "dataMax"]}
                      tick={CHART_AXIS_TICK}
                    />
                    <YAxis dataKey="elevation" tick={CHART_AXIS_TICK} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Line
                      type="linear"
                      dataKey="elevation"
                      stroke={CHART_SERIES_PRIMARY}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
